import { useState } from "react";
import { Mail, MapPin, Clock, Send, CheckCircle, ArrowRight, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useMarketConfig, MARKET_LABELS } from "@/utils/marketConfig";
import "@/styles/landing.css";

export default function ContactPage() {
  // Location card follows the market model: logged in -> account market,
  // logged out -> IP geo. Placeholder until resolved (never a wrong country).
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "general",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitted(true);
      toast.success("Message sent! We'll get back to you within 24 hours.");
    } catch (error) {
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-[var(--ap-line)] bg-white text-[15px] text-[var(--ap-ink)] outline-none transition-colors focus:border-[var(--ap-violet)]";

  const channels = [
    {
      icon: Mail,
      label: "Email",
      body: (
        <a href="mailto:support@placementos.ai" className="text-[var(--ap-violet)] font-semibold hover:underline">
          support@placementos.ai
        </a>
      ),
      caption: "We respond within 24 hours"
    },
    {
      icon: MapPin,
      label: "Location",
      body: <p className="text-[15px] font-semibold text-[var(--ap-ink)]">{mktReady ? (MARKET_LABELS[mkt.market] || "India") : "…"}</p>,
      caption: "Operating remotely, serving globally"
    },
    {
      icon: Clock,
      label: "Support Hours",
      body: <p className="text-[15px] font-semibold text-[var(--ap-ink)]">Monday – Saturday</p>,
      caption: "10:00 AM – 7:00 PM"
    }
  ];

  return (
    <div className="ap-root min-h-screen" data-testid="contact-page">

      {/* ============ HERO ============ */}
      <section className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section text-center max-w-[760px] mx-auto">
          <span className="ap-chip ap-rise ap-rise-1" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}>
            <MessageSquare className="w-4 h-4" /> We're here to help
          </span>
          <h1 className="ap-display font-extrabold mt-5 text-[var(--ap-ink)] ap-rise ap-rise-1" style={{ fontSize: "clamp(34px,5vw,60px)", lineHeight: 1.04, letterSpacing: "-0.03em" }}>
            Get in <span className="ap-grad-text">Touch</span>.
          </h1>
          <p className="text-[17px] md:text-[18px] text-[var(--ap-ink-2)] leading-relaxed mt-5 max-w-[600px] mx-auto ap-rise ap-rise-2">
            Have questions about PlacementOS? We'd love to hear from you. Our team is ready to help.
          </p>
        </div>
      </section>

      {/* ============ CONTACT CONTENT ============ */}
      <section className="ap-section py-10 pb-24">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-start">

          {/* ---- LEFT: contact channels ---- */}
          <div>
            <span className="ap-eyebrow">Contact Information</span>
            <h2 className="ap-h2 mt-3" style={{ fontSize: "clamp(24px,3vw,34px)" }}>
              Reach us <span className="ap-grad-text">directly</span>.
            </h2>

            <div className="space-y-4 mt-8">
              {channels.map((c) => (
                <div key={c.label} className="ap-card ap-pop p-5 flex items-start gap-4">
                  <span className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                    <c.icon className="w-6 h-6" />
                  </span>
                  <div>
                    <h3 className="ap-display font-bold text-[15px] text-[var(--ap-ink)] mb-1">{c.label}</h3>
                    {c.body}
                    <p className="text-[13px] text-[var(--ap-muted)] mt-1">{c.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ---- RIGHT: contact form ---- */}
          <div className="ap-card ap-mesh overflow-hidden p-6 md:p-9">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0" style={{ background: "var(--ap-fire)" }}>
                <Send className="w-5 h-5" />
              </span>
              <h2 className="ap-display font-extrabold text-[22px] md:text-[26px] text-[var(--ap-ink)]">
                Send us a <span className="ap-grad-text">Message</span>
              </h2>
            </div>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "rgba(22,163,74,.1)" }}>
                  <CheckCircle className="w-8 h-8 text-[var(--ap-green)]" />
                </div>
                <h3 className="ap-display text-[22px] font-bold text-[var(--ap-ink)] mb-2">Message Sent!</h3>
                <p className="text-[15px] text-[var(--ap-muted)]">We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => { setSubmitted(false); setFormData({ firstName: "", lastName: "", email: "", subject: "general", message: "" }); }}
                  className="mt-6 text-[var(--ap-violet)] font-semibold bg-transparent border-0 cursor-pointer hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Subject</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing & Payments</option>
                    <option value="partnership">Partnership</option>
                    <option value="feedback">Feedback</option>
                    <option value="privacy">Privacy & Data</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className={`${inputClass} resize-none`}
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="ap-btn ap-btn-fire ap-btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : <>Send Message <ArrowRight className="w-[18px] h-[18px]" /></>}
                </button>
              </form>
            )}
          </div>

        </div>
      </section>
    </div>
  );
}
